import React, { createContext, useContext, useState, useEffect } from 'react'
import { signIn, signOut, getUserId } from '../../lib/auth'
import { getIdData } from '../../lib/profiles'

const AuthContext = createContext()

export const useAuth = () => {
	const context = useContext(AuthContext)
	if (!context) {
		throw new Error('useAuth must be used within an AuthProvider')
	}
	return context
}

export const AuthProvider = ({ children }) => {
	const [user, setUser] = useState(null)
	const [loading, setLoading] = useState(true)

	// Initialize user from localStorage or database on app start
	useEffect(() => {
		async function initializeUser() {
			try {
				// First check if user data exists in localStorage
				const savedUser = localStorage.getItem('user')
				if (savedUser) {
					setUser(JSON.parse(savedUser))
					setLoading(false)
					return
				}

				// If no localStorage data, try to get from database
				const eplusData = localStorage.getItem('eplus')
				if (eplusData) {
					const userData = JSON.parse(eplusData)
					if (userData && userData.length > 0) {
						setUser(userData[0])
						localStorage.setItem('user', JSON.stringify(userData[0]))
						setLoading(false)
						return
					}
				}

				// Try to get current session
				const userId = await getUserId()
				if (userId) {
					const userprofile = await getIdData(userId)
					if (userprofile && userprofile.length > 0) {
						setUser(userprofile[0])
						localStorage.setItem('user', JSON.stringify(userprofile[0]))
						localStorage.setItem('login', JSON.stringify(true))
						localStorage.setItem('authenticated', JSON.stringify(true))
					}
				}
			} catch (error) {
				console.error('Error initializing user:', error)
			} finally {
				setLoading(false)
			}
		}

		initializeUser()
	}, [])

	const login = async (email, password) => {
		setLoading(true)
		try {
			const { data, error } = await signIn(email, password)

			if (error) {
				return { success: false, error: error.message }
			}

			if (data && data.user) {
				// Get user profile from database
				const userprofile = await getIdData(data.user.id)

				if (userprofile && userprofile.length > 0) {
					const userData = userprofile[0]
					setUser(userData)
					localStorage.setItem('user', JSON.stringify(userData))
					localStorage.setItem('companyId', userData.companyId)
					localStorage.setItem('login', JSON.stringify(true))
					localStorage.setItem('authenticated', JSON.stringify(true))
					return { success: true, user: userData }
				} else {
					return { success: false, error: 'User profile not found' }
				}
			} else {
				return { success: false, error: 'Authentication failed' }
			}
		} catch (error) {
			return { success: false, error: error.message }
		} finally {
			setLoading(false)
		}
	}

	const logout = async () => {
		try {
			await signOut()
		} catch (error) {
			console.error('Error during logout:', error)
		} finally {
			setUser(null)
			localStorage.removeItem('user')
			localStorage.removeItem('login')
			localStorage.removeItem('authenticated')
			localStorage.removeItem('profileURL')
			localStorage.removeItem('Name')
			localStorage.removeItem('eplus')
			localStorage.removeItem('companyId')
		}
	}

	const updateUser = (userData) => {
		const updatedUser = { ...user, ...userData }
		setUser(updatedUser)
		localStorage.setItem('user', JSON.stringify(updatedUser))
	}

	const hasRole = (requiredRole) => {
		if (!user) return false
		return user.role === requiredRole
	}

	const hasAnyRole = (requiredRoles) => {
		if (!user) return false
		return requiredRoles.includes(user.role)
	}

	const value = {
		user,
		loading,
		login,
		logout,
		updateUser,
		hasRole,
		hasAnyRole,
		isAuthenticated: !!user
	}

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
